import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, PauseCircle, PlayCircle, UserCog } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, EnumOption, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchTranslators,
  setTranslatorStatus,
  LOC_STR as S,
  type TranslatorRow,
} from './localizationExtraApi'

/**
 * A22.09 — Права переводчиков. Roster of translators with their assigned
 * languages, role (translator/reviewer/lead) and publish rights; suspend/
 * activate toggles access. Dormant until `/admin/localization/translators`
 * ships → fetcher fail-soft to empty. Placeholder permission USERS_VIEW.
 */
export function LocalizationTranslators() {
  const { toast } = useToast()

  const roleOptions = useMemo<EnumOption[]>(
    () => [
      { value: 'translator', label: S.roleTranslator },
      { value: 'reviewer', label: S.roleReviewer },
      { value: 'lead', label: S.roleLead },
    ],
    [],
  )

  const statusOptions = useMemo<EnumOption[]>(
    () => [
      { value: 'active', label: S.statusActive, status: 'active' },
      { value: 'suspended', label: S.statusSuspended, status: 'suspended' },
    ],
    [],
  )

  const columns = useMemo<AdminColumn<TranslatorRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      { key: 'languages', header: S.languages, type: 'tags' },
      { key: 'role', header: S.role, type: 'enum', options: roleOptions },
      { key: 'can_publish', header: S.canPublish, type: 'bool' },
      { key: 'strings_translated', header: S.stringsTranslated, type: 'number' },
      { key: 'status', header: S.status, type: 'enum', options: statusOptions },
      { key: 'added_at', header: S.addedAt, type: 'date' },
    ],
    [roleOptions, statusOptions],
  )

  const changeStatus = useCallback(
    async (row: TranslatorRow, status: string) => {
      try {
        await setTranslatorStatus(row.id, status)
        toast(S.statusChanged, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<TranslatorRow>[]>(
    () => [
      {
        id: 'suspend',
        label: S.suspend,
        icon: <PauseCircle size={14} />,
        danger: true,
        onSelect: (r) => changeStatus(r, 'suspended'),
        visible: (r) => r.status === 'active',
      },
      {
        id: 'activate',
        label: S.activate,
        icon: <PlayCircle size={14} />,
        onSelect: (r) => changeStatus(r, 'active'),
        visible: (r) => r.status === 'suspended',
      },
    ],
    [changeStatus],
  )

  const fetcher = useCallback(fetchTranslators, [])
  return (
    <>
      <PageHeader
        title={S.translatorsTitle}
        screenId="A22.09"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.back}
          </Link>
        }
      />
      <DataTable<TranslatorRow>
        screenId="A22.09"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <UserCog size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
